import { api } from "../api";
import type { InvoiceReceiptData } from "@/components/receipt/InvoiceReceiptDocument";

export interface AdminInvoice extends Invoice {
  user?: { id: string; firstName?: string; lastName?: string; email: string };
}

export interface Invoice {
  id: string;
  type: string;
  status: string;
  amountDue: number;
  amountPaid: number | null;
  schoolShare?: number | null;
  platformShare?: number | null;
  currency: string;
  dueDate: string | null;
  paidAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface InitiatePaymentResponse {
  authorizationUrl: string;
  reference: string;
}

export type ReceiptVerificationResult =
  | {
      valid: true;
      tenantName: string;
      libraryFullName?: string | null;
      libraryAddress?: string | null;
      invoiceNumber: string;
      description: string;
      amountPaid: number;
      currency: string;
      paidAt: string;
      patronName: string;
      type?: string;
      receiptLabel?: string;
      resourceTitle?: string;
      patronTypeNameAtPurchase?: string;
      accessDurationDays?: number | null;
      accessExpiresAt?: string | null;
    }
  | { valid: false; message: string };

export interface UserInvoiceListResponse {
  invoices: Invoice[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}

export interface AdminInvoiceListResponse {
  invoices: AdminInvoice[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}

export const invoicesApi = {
  list: (params?: { cursor?: string; limit?: number; status?: string }) =>
    api.get<UserInvoiceListResponse>("/invoices", { params }).then((r) => r.data),

  getById: (id: string): Promise<Invoice & { payments?: unknown[] }> =>
    api.get(`/invoices/${id}`).then((r) => r.data),

  getReceipt: (id: string): Promise<InvoiceReceiptData> =>
    api.get(`/invoices/${id}/receipt`).then((r) => r.data),

  initiatePayment: (invoiceId: string, provider?: string): Promise<InitiatePaymentResponse> =>
    api.post(`/invoices/${invoiceId}/initiate-payment`, { provider }).then((r) => r.data),

  /** Re-verify with Paystack — use when payment completed but system still shows pending (e.g. session expiry) */
  verifyPayment: (
    invoiceId: string
  ): Promise<{ success: boolean; message: string; status?: string; reference?: string }> =>
    api.post(`/invoices/${invoiceId}/verify-payment`).then((r) => r.data),

  // Staff-only (requires invoices.view / invoices.manage)
  listAdmin: (params?: { userId?: string; status?: string; limit?: number; cursor?: string }) =>
    api.get<AdminInvoiceListResponse>("/invoices/admin", { params }).then((r) => r.data),

  searchPatrons: (q: string) =>
    api
      .get<Array<{ id: string; email: string; name: string }>>("/invoices/admin/patrons", {
        params: { q },
      })
      .then((r) => r.data),

  getReceiptAdmin: (id: string): Promise<InvoiceReceiptData> =>
    api.get(`/invoices/admin/${id}/receipt`).then((r) => r.data),

  /** Public — no login required (QR / shared link). */
  verifyReceipt: (invoiceId: string, token: string): Promise<ReceiptVerificationResult> =>
    api
      .get<ReceiptVerificationResult>("/invoice-receipts/verify", {
        params: { i: invoiceId, t: token },
      })
      .then((r) => r.data),

  create: (data: {
    userId: string;
    type: string;
    amountDue: number;
    description?: string;
    currency?: string;
    dueDate?: string;
    schoolShare?: number;
    platformShare?: number;
  }) => api.post("/invoices", data).then((r) => r.data),
};
